/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Text, Popover, SVGIcon, Navigation, theme } from "@nordcloud/gnui";
import { PathName } from "~/tools";

export enum ACTION {
  ADD = "add",
  DELETE = "delete",
  EDIT = "edit",
  CUSTOM_TRIGGER = "customTrigger",
}

type Item = {
  label: string;
  action: ACTION;
  onClick: () => void;
  customIcon?: PathName;
  disabled?: boolean;
};

type PopoverControlsProps = {
  items: Item[];
  trigger: React.ReactNode;
};

const disabledStyle = (disabled = false) =>
  disabled
    ? {
        fill: theme.color.interactive.disabled,
        color: theme.color.interactive.disabled,
      }
    : {};

export function PopoverControls({ items, trigger }: PopoverControlsProps) {
  return (
    <Popover trigger={trigger} alignRight>
      <Navigation.Container popoverMenu>
        {items.map((item) => (
          <Navigation.Item
            key={item.label}
            onClick={item.disabled ? undefined : item.onClick}
            css={{
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <RenderIcon
              action={item.action}
              disabled={item.disabled}
              customIcon={item.customIcon}
            />
            <Text tag="span" css={disabledStyle(item.disabled)}>
              {item.label}
            </Text>
          </Navigation.Item>
        ))}
      </Navigation.Container>
    </Popover>
  );
}

type RenderIconProps = {
  action: ACTION;
  disabled?: boolean;
  customIcon?: PathName;
};

function RenderIcon({ action, disabled, customIcon }: RenderIconProps) {
  const icon = getIcon(action, customIcon);

  if (icon) {
    return <SVGIcon name={icon} size="sm" css={disabledStyle(disabled)} />;
  }

  return null;
}

function getIcon(action: ACTION, customIcon?: PathName) {
  if (customIcon != null) {
    return customIcon;
  }

  switch (action) {
    case ACTION.CUSTOM_TRIGGER:
      return "gear";
    case ACTION.ADD:
      return "plus";
    case ACTION.DELETE:
      return "trash";
    case ACTION.EDIT:
      return "edit";
    default:
      return "";
  }
}
